/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { publicApiNames } from "publicApis/constants";
import { apiFactory } from "api/external/ApiFactory";
import { callExitCallback, setActiveVerbCallback } from "utils/QuickToolsUtils";
import { isNumber, isFunction, isBoolean } from "utils/APIUtils";
import { featureLifecycleHookController } from "./controller/featureLifecycleHookController";
import { featureLifecyleHooks, initialQTOrderedVerbs } from "constants/enums";
import { getActiveVerb as getActiveQTVerb } from "selectors/quickToolsSelectors";
import { modifyCursorApis } from "publicApis/OldPublicApis";
import { previewEvents } from "publicApis/Events/previewEvents";
import { eventNames } from "publicApis/constants.js";

const getQuickToolsApis = stores => {
  function show(visible) {
    if (isBoolean(visible)) {
      stores.documentStore.setQuickToolsVisibility(visible);
    }
  }

  function beforeToolActivated(callback, opts = {}) {
    if (isFunction(callback)) {
      if (opts.isSave) {
        featureLifecycleHookController.on(featureLifecyleHooks.SAVE_BEFORE_QUICK_TOOL_ACTIVATED, callback);
      } else {
        featureLifecycleHookController.on(featureLifecyleHooks.BEFORE_QUICK_TOOL_ACTIVATED, callback);
      }
    }
  }

  function registerSecondaryComponent(config) {
    if (config !== null && typeof config === "object" && isFunction(config.body)) {
      stores.documentStore.registerSecondaryComponent(config);
    }
  }

  function setSecondaryFlyout(config) {
    let isValidConfig = true;
    if (config !== null && typeof config === "object" && isFunction(config.body)) {
      if (config.hasOwnProperty("offset") && !isNumber(config.offset)) {
        isValidConfig = false;
      }
      if (config.hasOwnProperty("shouldAutoCloseFlyout") && !isBoolean(config.shouldAutoCloseFlyout)) {
        isValidConfig = false;
      }
    } else {
      isValidConfig = false;
    }

    if (isValidConfig) {
      stores.documentStore.setSecondaryFlyout(config);
    }
  }

  function resetSecondaryFlyout() {
    stores.documentStore.resetSecondaryFlyout();
  }

  function disableQuickTools(config) {
    if (isBoolean(config.disable) && typeof config.notificationMsg === "string") {
      stores.quickToolsStore.updatePropertiesForAllVerbs(config);
    }
  }

  function getActiveTool() {
    const { quickToolsStore } = stores;
    const activeVerb = getActiveVerb(stores);
    if (activeVerb && activeVerb !== "" && quickToolsStore.quickToolsVerbs[activeVerb]) {
      const groupIndex = quickToolsStore.quickToolsVerbs[activeVerb].verbGroupIndex;
      return initialQTOrderedVerbs[groupIndex];
    }
  }

  function getActiveVerb() {
    return getActiveQTVerb(stores);
  }

  function reset(opts = {}) {
    const { quickToolsStore } = stores;
    const { resetActiveVerb } = opts;
    const activeVerb = getActiveVerb(stores);
    if (activeVerb) {
      callExitCallback(activeVerb).then(() => {
        modifyCursorApis(stores)
          .apiGetter()
          .resetCursor();
        quickToolsStore.resetQuickTools(opts);
      });
    } else {
      quickToolsStore.resetQuickTools(opts);
    }
    if (resetActiveVerb) {
      quickToolsStore.resetActiveVerb();
    }
  }

  function setActiveVerb(verb, opts = {}) {
    setActiveVerbCallback(verb, opts);
  }

  function subscribe(handler) {
    previewEvents.on(eventNames.QUICK_TOOL_VERB_CHANGE, handler);
  }

  return {
    show,
    beforeToolActivated,
    registerSecondaryComponent,
    setSecondaryFlyout,
    resetSecondaryFlyout,
    getActiveTool,
    reset,
    getActiveVerb,
    setActiveVerb,
    subscribe,
    disableQuickTools
  };
};

export const registerQuickToolsApis = stores => {
  apiFactory.registerApi(publicApiNames.QUICKTOOLS, clientName => getQuickToolsApis(stores), true);
};
